import React from 'react';
import PropTypes from 'prop-types';

import Avatar from 'components/Avatar/Avatar';
import Button from 'components/Button/Button';

import RefundPopup from './RefundPopup';

import EventRepeat20 from 'icons/EventRepeat20';
import Undo20 from 'icons/Undo20';

import { useAuth } from 'utils/auth';
import classNames from 'utils/classNames';
import formatAmount, { formatDate, getTimeFromDate } from 'utils/formatAmount';
import { actions } from 'utils/permissions';

import styles from './payment-history-item.scss';

export default function PaymentHistoryItem(props) {
    const auth = useAuth();

    const { payment } = props;

    const { id, amount, title, status, updated_at, is_recurrent, refunded_amount, extra_data } = payment;

    const [popup, setPopup] = React.useState(null);

    function handleRetryClick() {}

    function getPossibleRefundAmount() {
        if (refunded_amount) {
            return amount - refunded_amount;
        }

        return amount;
    }

    function handleRefundClick() {
        setPopup(<RefundPopup paymentId={id} amount={getPossibleRefundAmount()} onClosed={handlePopupClosed} />);
    }

    function handlePopupClosed() {
        setPopup(null);
    }

    /**
     * Returns refund text based on refund type: full/partial.
     */
    function getRefundedText() {
        if (status === 'REFUNDED') {
            return 'Оформлен полный возврат:';
        }

        if (status === 'PARTIAL_REFUNDED') {
            return 'Частичный возврат:';
        }
    }

    /**
     * Returns refund action if available.
     */
    function getRefundAction() {
        if (['COMPLETED', 'PARTIAL_REFUNDED'].includes(status) && auth.hasPermission(actions.REFUND_PAYMENT)) {
            return (
                <Button size={'s'} icon={<Undo20 />} level={'secondary'} text={'Вернуть'} onClick={handleRefundClick} />
            );
        }

        return null;
    }

    /**
     * Returns retry action if available.
     */
    function getRetryAction() {
        if (auth.hasPermission(actions.RETRY_PAYMENT)) {
            return <Button size={'s'} level={'secondary'} text={'Повторить'} onClick={handleRetryClick} />;
        }
    }

    /**
     * Returns payment actions based on it's status.
     */
    function getPaymentsActions() {
        switch (status) {
            case 'FAILED':
                return getRetryAction();
            case 'COMPLETED':
            case 'REFUNDED':
            case 'PARTIAL_REFUNDED':
                return getRefundAction();
            default:
                return null;
        }
    }

    /**
     * Creates and returns payment actions to insert into the DOM.
     */
    function preparePaymentActionsToRender() {
        const actions = getPaymentsActions();

        if (actions) {
            return <div className={styles.action}>{actions}</div>;
        }

        return null;
    }

    function getTerminalName() {
        return extra_data.tinkoff_payment_id ? 'Тинькофф' : 'ЮКасса';
    }

    const error = status === 'FAILED';
    const refunded = status === 'REFUNDED' || status === 'PARTIAL_REFUNDED';
    const paymentActions = preparePaymentActionsToRender();
    const actionsHidden = paymentActions === null;
    const dateAndTerminal = `${formatDate(updated_at)} в ${getTimeFromDate(updated_at)}, ${getTerminalName()}`;

    return (
        <div className={styles.wrapper}>
            {popup}
            <div className={styles.terminalIcon}>
                <Avatar text={title} size="m" />
            </div>
            <div className={styles.content}>
                <h2 className={styles.title}>{title}</h2>
                {error && <span className={styles.error}>Не удалось завершить оплату.</span>}
                {refunded && <span className={styles.refunded}>{`${getRefundedText()} ${refunded_amount}`} ₽</span>}
                <span className={styles.date}>{dateAndTerminal}</span>
            </div>
            <div
                className={classNames(styles.right, {
                    [styles.hidden]: actionsHidden,
                })}
            >
                {paymentActions}
                {is_recurrent && (
                    <div
                        className={classNames(styles.recurrent, {
                            [styles.error]: error,
                        })}
                    >
                        <EventRepeat20 />
                    </div>
                )}
                <div
                    className={classNames(styles.amount, {
                        [styles.error]: error,
                    })}
                >
                    <span>{formatAmount(amount)}</span>
                    <span className={styles.currency}>₽</span>
                </div>
            </div>
        </div>
    );
}

PaymentHistoryItem.propTypes = {
    data: PropTypes.object,
    payment: PropTypes.shape({
        active: PropTypes.bool,
        /**
         * Payment amount.
         */
        amount: PropTypes.number,
        client_id: PropTypes.number,
        created_at: PropTypes.string,
        currency: PropTypes.string,
        /**
         * Form customization id.
         */
        customization_id: PropTypes.number,
        deactivated_at: PropTypes.string,
        external_status: PropTypes.string,
        form_id: PropTypes.number,
        id: PropTypes.number.isRequired,
        is_recurrent: PropTypes.bool,
        is_unlimited: PropTypes.bool,
        refunded_amount: PropTypes.number,
        regular_amount: PropTypes.number,
        status: PropTypes.string,
        subscription_id: PropTypes.number,
        success: PropTypes.bool,
        title: PropTypes.string,
        updated_at: PropTypes.string,
        extra_data: PropTypes.shape({
            tinkoff_payment_id: PropTypes.string,
        }),
    }),
};
